import { ChangeDetectionStrategy, Component, computed, signal, ViewEncapsulation } from '@angular/core';
import { OptionsStackingValue, PlotOptions } from 'highcharts';
import { SeriesAreaOptions } from 'highcharts/highcharts.src';
import { Serialize } from '../VisualDataSerializer';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { ChartVisual, ChartVisualComponent } from './ChartVisual.component';
import { ChartStyleEditorComponent } from './ChartVisualStyleEditor.component';
import { PbiChartTypes } from '../../../shared/chart/chart';

@Component({
  selector: 'pbir-visual__areaChart',
  templateUrl: './ChartVisual.component.html',
  styleUrls: ['./ChartVisual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AreaChartVisualComponent
  extends ChartVisualComponent<
    SeriesAreaOptions,
    AreaChartVisual.Options,
    AreaChartVisual.State,
    AreaChartVisual.Style> {
  get measuresRoleType() { return AreaChartVisual.roles.measures; }
  get groupingRoleType() { return AreaChartVisual.roles.axis; }
  get legendRoleType() { return AreaChartVisual.roles.legend; }
  
  readonly displayType = computed(() => this.style()?.displayType() ?? AreaChartVisual.DisplayType.Default);
  
  readonly default = computed(() => this.displayType() == AreaChartVisual.DisplayType.Default);
  readonly clustered = computed(() => this.displayType() == AreaChartVisual.DisplayType.Clustered);
  readonly stacked = computed(() => this.displayType() == AreaChartVisual.DisplayType.Stacked);
  readonly stacked100p = computed(() => this.displayType() == AreaChartVisual.DisplayType.Stacked100p);

  get chartType() {
    return 'area';
  }
  
  computePlotOptions(): PlotOptions {
    let stacking: OptionsStackingValue =
      (this.clustered())
        ? undefined
        : (this.stacked100p())
          ? 'percent'
          : 'normal';

    return {
      ...super.computePlotOptions(),
      
      area: {
        animation: !this.disableAnimations,

        stacking,
      },
    };
  }

  useSingleSeriesStack() {
    return !this.default();
  }

  tooltipsIncludePercentage() {
    return this.stacked100p();
  }
}

@Component({
  selector: 'pbir-visual__areaChart__style-editor',
  template: `
    <pbir-visual__chart-style-editor [busy]="busy()" [visual]="visual()">
      <ng-container appearanceAfter>
        <label>Chart:</label>
        <mat-select class="trg-outline user-select-none" panelClass="trg-outline-dropdown"
                    [disabled]="busy()"
                    [value]="displayType()"
                    (valueChange)="displayType.set($event ? $event : null)">
          @for (o of DisplayTypeOptions; track o.value) {
            <mat-option [value]="o.value">{{o.title}}</mat-option>
          }
        </mat-select>
      </ng-container>
    </pbir-visual__chart-style-editor>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AreaChartVisualStyleEditorComponent extends ChartStyleEditorComponent<
  AreaChartVisual.Options,
  AreaChartVisual.State,
  AreaChartVisual.Style> {
  readonly DisplayTypeOptions = [
    {
      value: AreaChartVisual.DisplayType.Default,
      title: 'Default'
    },
    {
      value: AreaChartVisual.DisplayType.Clustered,
      title: 'Clustered'
    },
    {
      value: AreaChartVisual.DisplayType.Stacked,
      title: 'Stacked'
    },
    {
      value: AreaChartVisual.DisplayType.Stacked100p,
      title: '100% Stacked'
    }
  ];

  get displayType() { return this.style().displayType; }
}

export namespace AreaChartVisual {
  export enum DisplayType {
    Default = 'simple',
    Clustered = 'clustered',
    Stacked = 'stacked',
    Stacked100p = 'stacked100%',
  }

  export class State extends ChartVisual.State { }

  export class Style extends ChartVisual.Style {
    @Serialize({ defaultValue: DisplayType.Default })
    readonly displayType = signal(DisplayType.Default);
  }

  export interface Options extends ChartVisual.Options {
  }

  export const roles = VisualFieldRoleTypeMap.create({
    axis: new VisualFieldRoleType('axis', VisualFieldRoleType.Defaults.groupingAxis),
   
    legend: new VisualFieldRoleType('legend', {
      ...VisualFieldRoleType.Defaults.detailLegend,
      isHidden: v => v.style().displayType() == DisplayType.Default,
      isEnabled: v => v.fieldRoles.get(roles.measures).fields.length <= 1,
      limits: v => ({ min: 0, max: 1 })
    }),

    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,
      limits: v =>
        (v.options().displayType == DisplayType.Default || v.fieldRoles.get(roles.legend).fields.length > 0)
          ? { min: 1, max: 1 }
          : { min: 1 }
    }),
  });

  export const type = ChartVisual.defineType<AreaChartVisualComponent>(
    AreaChartVisualComponent,
    'areaChart',
    roles,
    {
      visualOptions: {
        dataLabelPosition: ChartVisual.DataLabelsPosition.Outside,

        legendEnabledByDefault: false,
        dataLabelsEnabledByDefault: false,
      },
      createState: () => new State(),
      createStyle: () => new Style(),
      styleEditor: AreaChartVisualStyleEditorComponent,
      showOverlayUntilRendered: true,
      getPbiChartType: v => PbiChartTypes.areaChart,
    });
}
